import { useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    FormControlLabel,
    Grid,
    Paper,
    Switch,
    TextField,
    Typography,
} from '@mui/material';
import { Save as SaveIcon } from '@mui/icons-material';
import {
    getGlobalConfig,
    updateReportMatchingSettings,
} from '../../services/api';

function normalizeReportMatchingConfig(reportMatching) {
    return {
        description: reportMatching?.description
            || 'Automatically queue gap recommendations for the top matched reports in each displayed bucket after matching completes.',
        auto_gap_recommendation_enabled: Boolean(reportMatching?.auto_gap_recommendation_enabled),
        auto_recommendation_standard_top_n: Number(reportMatching?.auto_recommendation_standard_top_n ?? 1) || 0,
        auto_recommendation_custom_top_n: Number(reportMatching?.auto_recommendation_custom_top_n ?? 1) || 0,
        auto_recommendation_standard_other_theme_top_n: Number(reportMatching?.auto_recommendation_standard_other_theme_top_n ?? 1) || 0,
    };
}

function ReportMatchingAutoRecommendationConfig({ reportMatching, onUpdate, showNotification }) {
    const normalizedConfig = useMemo(() => normalizeReportMatchingConfig(reportMatching), [reportMatching]);
    const [settings, setSettings] = useState(normalizedConfig);
    const [hasChanges, setHasChanges] = useState(false);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        setSettings(normalizedConfig);
        setHasChanges(false);
    }, [normalizedConfig]);

    const handleToggle = (event) => {
        setSettings((prev) => ({
            ...prev,
            auto_gap_recommendation_enabled: event.target.checked,
        }));
        setHasChanges(true);
    };

    const handleNumberChange = (key) => (event) => {
        const rawValue = event.target.value;
        const numericValue = rawValue === '' ? '' : Math.max(0, Number(rawValue));
        setSettings((prev) => ({
            ...prev,
            [key]: numericValue,
        }));
        setHasChanges(true);
    };

    const handleReset = () => {
        setSettings(normalizedConfig);
        setHasChanges(false);
    };

    const handleSave = async () => {
        try {
            setSaving(true);
            await updateReportMatchingSettings({
                description: settings.description,
                auto_gap_recommendation_enabled: settings.auto_gap_recommendation_enabled,
                auto_recommendation_standard_top_n: Number(settings.auto_recommendation_standard_top_n || 0),
                auto_recommendation_custom_top_n: Number(settings.auto_recommendation_custom_top_n || 0),
                auto_recommendation_standard_other_theme_top_n: Number(settings.auto_recommendation_standard_other_theme_top_n || 0),
            });
            const config = await getGlobalConfig();
            onUpdate(config);
            setHasChanges(false);
            showNotification('Auto gap recommendation settings saved');
        } catch (err) {
            showNotification(err.response?.data?.detail || 'Failed to save', 'error');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Box>
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                    <Typography variant="h6" fontWeight={600}>
                        Auto Gap Recommendation
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {settings.description}
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    {hasChanges && (
                        <Button variant="outlined" color="inherit" onClick={handleReset}>
                            Reset
                        </Button>
                    )}
                    <Button
                        variant="contained"
                        startIcon={<SaveIcon />}
                        onClick={handleSave}
                        disabled={!hasChanges || saving}
                    >
                        {saving ? 'Saving...' : 'Save Changes'}
                    </Button>
                </Box>
            </Box>

            {hasChanges && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    You have unsaved changes. Click "Save Changes" to persist them.
                </Alert>
            )}

            <Paper sx={{ p: 3 }}>
                <FormControlLabel
                    control={
                        <Switch
                            checked={settings.auto_gap_recommendation_enabled}
                            onChange={handleToggle}
                        />
                    }
                    label="Enable Auto Gap Recommendation"
                    sx={{ mb: 2 }}
                />

                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    When enabled, each matching job automatically queues gap recommendation generation for the configured top reports from each displayed bucket after matching completes.
                </Typography>

                <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                        <TextField
                            fullWidth
                            type="number"
                            label="Standard Bucket Auto Top N"
                            value={settings.auto_recommendation_standard_top_n}
                            onChange={handleNumberChange('auto_recommendation_standard_top_n')}
                            inputProps={{ min: 0, max: 20 }}
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <TextField
                            fullWidth
                            type="number"
                            label="Custom Bucket Auto Top N"
                            value={settings.auto_recommendation_custom_top_n}
                            onChange={handleNumberChange('auto_recommendation_custom_top_n')}
                            inputProps={{ min: 0, max: 20 }}
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <TextField
                            fullWidth
                            type="number"
                            label="Other Theme Bucket Auto Top N"
                            value={settings.auto_recommendation_standard_other_theme_top_n}
                            onChange={handleNumberChange('auto_recommendation_standard_other_theme_top_n')}
                            inputProps={{ min: 0, max: 20 }}
                        />
                    </Grid>
                </Grid>
            </Paper>
        </Box>
    );
}

export default ReportMatchingAutoRecommendationConfig;
